import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, PointElement, LineElement } from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, PointElement, LineElement);

const DIRECT_COLOR = '#4F46E5';
const OTHERS_COLOR = '#9CA3AF';
const SENTIMENT_COLORS = {
  positive: '#10B981',
  negative: '#EF4444',
  neutral: '#F59E0B',
  mixed: '#8B5CF6',
  unknown: '#6B7280',
};

const formatNumber = (n) => Number(n || 0).toLocaleString();
const formatRupiah = (n) => `Rp ${Number(n || 0).toLocaleString()}`;

export default function MentionAnalysis() {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = () => {
    const token = localStorage.getItem('token');
    const params = {};
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;

    setLoading(true);
    setError('');
    axios.get('/api/dashboard/mention-comparison', {
      params,
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        setData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Mention comparison fetch error:', err.response?.data || err.message);
        setError('Gagal memuat data analisis.');
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isValid = !fromDate || !toDate || new Date(fromDate) <= new Date(toDate);

  const topBrands = data ? data.brands.slice(0, 20) : [];
  const brandChartData = data ? {
    labels: topBrands.map((b) => b.brand),
    datasets: [{
      label: 'Jumlah Artikel',
      data: topBrands.map((b) => b.count),
      backgroundColor: topBrands.map((b) => (b.bucket === 'direct' ? DIRECT_COLOR : OTHERS_COLOR)),
    }],
  } : null;

  const trendChartData = data ? {
    labels: data.trend.map((d) => d.date),
    datasets: [
      {
        label: 'Direct Mention',
        data: data.trend.map((d) => d.direct || 0),
        borderColor: DIRECT_COLOR,
        backgroundColor: DIRECT_COLOR,
        tension: 0.3,
      },
      {
        label: 'Kategori Lainnya',
        data: data.trend.map((d) => d.others || 0),
        borderColor: OTHERS_COLOR,
        backgroundColor: OTHERS_COLOR,
        tension: 0.3,
      },
    ],
  } : null;

  const sentimentKeys = ['positive', 'neutral', 'negative', 'mixed', 'unknown'];
  const tonalityChartData = data ? {
    labels: ['Direct Mention', 'Kategori Lainnya'],
    datasets: sentimentKeys.map((key) => ({
      label: key.charAt(0).toUpperCase() + key.slice(1),
      data: [data.tonality.direct[key] || 0, data.tonality.others[key] || 0],
      backgroundColor: SENTIMENT_COLORS[key],
    })),
  } : null;

  const platformLabels = data ? Array.from(new Set([
    ...data.platforms.direct.map((p) => p.platform),
    ...data.platforms.others.map((p) => p.platform),
  ])) : [];
  const platformChartData = data ? {
    labels: platformLabels,
    datasets: [
      {
        label: 'Direct Mention',
        data: platformLabels.map((label) => data.platforms.direct.find((p) => p.platform === label)?.count || 0),
        backgroundColor: DIRECT_COLOR,
      },
      {
        label: 'Kategori Lainnya',
        data: platformLabels.map((label) => data.platforms.others.find((p) => p.platform === label)?.count || 0),
        backgroundColor: OTHERS_COLOR,
      },
    ],
  } : null;

  return (
    <div className="w-full p-0">
      <h2 className="text-2xl font-bold text-gray-900 mb-6 px-4">Analisis: Direct Mention vs Kategori Lainnya</h2>

      <div className="bg-white shadow rounded-lg p-5 mb-6 mx-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tanggal Dari</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tanggal Sampai</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <button
            onClick={fetchData}
            disabled={!isValid}
            className={`py-2 px-5 rounded-md font-medium ${isValid ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-300 text-gray-600 cursor-not-allowed'}`}
          >
            Terapkan
          </button>
          {(fromDate || toDate) && (
            <button
              onClick={() => { setFromDate(''); setToDate(''); fetchData(); }}
              className="py-2 px-5 rounded-md font-medium text-gray-700 border border-gray-300 hover:bg-gray-100"
            >
              Reset
            </button>
          )}
        </div>
        {!isValid && <p className="mt-2 text-sm text-red-600">Tanggal sampai harus lebih besar atau sama dengan tanggal dari.</p>}
        <p className="mt-3 text-xs text-gray-500">Default menampilkan data 7 hari terakhir jika tanggal tidak diisi.</p>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : error ? (
        <div className="text-center text-red-600 py-8">{error}</div>
      ) : (
        <div className="w-full space-y-8">
          {/* Summary cards */}
          <section className="px-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Ringkasan</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white shadow rounded-lg p-5 border-l-4" style={{ borderColor: DIRECT_COLOR }}>
                <h4 className="text-lg font-medium text-gray-900 mb-3">Direct Mention</h4>
                <div className="text-3xl font-bold" style={{ color: DIRECT_COLOR }}>{formatNumber(data.totals.direct.count)}</div>
                <div className="text-sm text-gray-600 mb-4">artikel ({data.totals.direct.percentage}% dari total)</div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div><div className="text-gray-500">Ad Value</div><div className="font-medium">{formatRupiah(data.totals.direct.ad_value)}</div></div>
                  <div><div className="text-gray-500">PR Value</div><div className="font-medium">{formatRupiah(data.totals.direct.pr_value)}</div></div>
                  <div><div className="text-gray-500">Reach</div><div className="font-medium">{formatNumber(data.totals.direct.media_reach)}</div></div>
                </div>
              </div>

              <div className="bg-white shadow rounded-lg p-5 border-l-4" style={{ borderColor: OTHERS_COLOR }}>
                <h4 className="text-lg font-medium text-gray-900 mb-3">Kategori Lainnya</h4>
                <div className="text-3xl font-bold text-gray-700">{formatNumber(data.totals.others.count)}</div>
                <div className="text-sm text-gray-600 mb-4">artikel ({data.totals.others.percentage}% dari total)</div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div><div className="text-gray-500">Ad Value</div><div className="font-medium">{formatRupiah(data.totals.others.ad_value)}</div></div>
                  <div><div className="text-gray-500">PR Value</div><div className="font-medium">{formatRupiah(data.totals.others.pr_value)}</div></div>
                  <div><div className="text-gray-500">Reach</div><div className="font-medium">{formatNumber(data.totals.others.media_reach)}</div></div>
                </div>
              </div>
            </div>
          </section>

          {/* Trend */}
          <section className="px-4">
            <div className="bg-white shadow rounded-lg p-5">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Tren Volume Harian</h3>
              {data.trend.length > 0 ? (
                <div className="h-64 w-full">
                  <Line data={trendChartData} options={{ responsive: true, maintainAspectRatio: false }} />
                </div>
              ) : (
                <div className="text-gray-500 text-center py-8">Tidak ada data</div>
              )}
            </div>
          </section>

          {/* Brand breakdown */}
          <section className="px-4">
            <div className="bg-white shadow rounded-lg p-5">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Top 20 Brand</h3>
              {topBrands.length > 0 ? (
                <div className="h-[32rem] w-full">
                  <Bar
                    data={brandChartData}
                    options={{
                      indexAxis: 'y',
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: { y: { ticks: { autoSkip: false } } },
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          callbacks: {
                            label: (ctx) => {
                              const b = topBrands[ctx.dataIndex];
                              return `${b.brand} (${b.category}): ${b.count.toLocaleString()} artikel`;
                            },
                          },
                        },
                      },
                    }}
                  />
                </div>
              ) : (
                <div className="text-gray-500 text-center py-8">Tidak ada data</div>
              )}
              <p className="mt-3 text-xs text-gray-500">Warna ungu = Direct Mention, abu-abu = brand lainnya.</p>
            </div>
          </section>

          {/* Tonality */}
          <section className="px-4">
            <div className="bg-white shadow rounded-lg p-5">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Tonalitas: Direct Mention vs Lainnya</h3>
              <div className="h-72 w-full">
                <Bar
                  data={tonalityChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: { x: { stacked: true }, y: { stacked: true } },
                  }}
                />
              </div>
              <p className="mt-3 text-xs text-gray-500">Catatan: sebagian besar artikel belum memiliki label tonalitas ("unknown").</p>
            </div>
          </section>

          {/* Platform breakdown */}
          <section className="px-4">
            <div className="bg-white shadow rounded-lg p-5">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Platform: Direct Mention vs Lainnya</h3>
              {platformLabels.length > 0 ? (
                <div className="h-64 w-full">
                  <Bar data={platformChartData} options={{ responsive: true, maintainAspectRatio: false }} />
                </div>
              ) : (
                <div className="text-gray-500 text-center py-8">Tidak ada data</div>
              )}
            </div>
          </section>

          {/* Brand table */}
          <section className="px-4 pb-8">
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <h3 className="text-lg font-medium text-gray-900 px-5 pt-5 mb-2">Detail per Brand</h3>
              <div className="overflow-x-auto max-h-[32rem] overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Brand</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Artikel</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">%</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ad Value</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">PR Value</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Reach</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.brands.map((b) => (
                      <tr key={`${b.category}::${b.brand}`} className={b.bucket === 'direct' ? 'bg-indigo-50' : ''}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{b.brand}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 text-right">{formatNumber(b.count)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 text-right">{b.percentageOfTotal}%</td>
                        <td className="px-4 py-3 text-sm text-gray-700 text-right">{formatRupiah(b.ad_value)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 text-right">{formatRupiah(b.pr_value)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 text-right">{formatNumber(b.media_reach)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
